<template>
  <div class="recharge-points">
    <!-- 顶部信息卡片 -->
    <div class="header-section">
      <el-card class="balance-card">
        <div class="balance-content">
          <div class="balance-info">
            <div class="balance-label">当前积分余额</div>
            <div class="balance-value">{{ balance.points_balance }} <span class="unit">分</span></div>
          </div>
          <el-button 
            type="primary" 
            text 
            @click="loadBalance" 
            class="refresh-btn"
            :loading="refreshing"
          >
            <i class="el-icon-refresh"></i>
            刷新余额
          </el-button>
        </div>
      </el-card>
    </div>

    <!-- 主充值区域 -->
    <el-row :gutter="20">
      <el-col :xs="24" :lg="16">
        <el-card class="recharge-card">
          <template #header>
            <div class="card-header">
              <h3 class="card-title">
                <i class="el-icon-wallet"></i>
                积分充值
              </h3>
            </div>
          </template>

          <!-- 使用说明 -->
          <el-alert 
            type="warning" 
            :closable="false" 
            class="usage-alert"
            title="5分钟计费说明"
            description="云资源以5分钟为一个计费单元；释放或到期结算时，已使用不足5分钟按5分钟计费。创建时预冻结一小时额度，最终按实际计费单元结算，多余冻结积分会解冻。1积分等于1分钱。" 
          />

          <!-- 充值表单 -->
          <div class="recharge-form">
            <div class="form-section">
              <div class="section-title">
                <i class="el-icon-user"></i>
                账户信息
              </div>
              <el-form-item>
                <el-input 
                  v-model="form.email" 
                  disabled
                  size="large"
                  :prefix-icon="ElIconMessage"
                />
              </el-form-item>
            </div>

            <div class="form-section">
              <div class="section-title">
                <i class="el-icon-coin"></i>
                选择充值金额
              </div>
              
              <!-- 快捷金额选择 -->
              <div class="quick-amounts">
                <div 
                  v-for="amount in quickAmounts" 
                  :key="amount.points"
                  class="amount-option"
                  :class="{ active: form.points === amount.points }"
                  @click="selectAmount(amount)"
                >
                  <div class="points">{{ amount.points }} 积分</div>
                  <div class="money">¥{{ amount.money }}</div>
                </div>
              </div>

              <!-- 自定义金额 -->
              <div class="custom-amount">
                <div class="custom-label">自定义金额</div>
                <div class="custom-inputs">
                  <el-input-number 
                    v-model="form.points" 
                    :min="100" 
                    :step="100"
                    size="large"
                    controls-position="right"
                    placeholder="请输入积分"
                  />
                  <div class="amount-display">
                    <span class="amount-text">≈ {{ amountYuan }} 元</span>
                    <span class="exchange-rate">(1元 = 100积分)</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- 操作按钮 -->
            <div class="action-buttons">
              <div class="primary-actions">
                <el-button 
                  type="primary" 
                  size="large" 
                  @click="openPayment('wechat')"
                  class="recharge-btn wechat-btn"
                  :loading="submitting"
                >
                  <i class="el-icon-chat-dot-round"></i>
                  微信充值 (¥{{ amountYuan }})
                </el-button>
                <el-button 
                  type="primary" 
                  size="large" 
                  @click="openPayment('alipay')"
                  class="recharge-btn alipay-btn"
                  :loading="submitting"
                >
                  <i class="el-icon-money"></i>
                  支付宝充值 (¥{{ amountYuan }})
                </el-button>
              </div>

              <div class="secondary-actions">
                <el-button 
                  size="large" 
                  @click="loadRecords"
                  class="record-btn"
                >
                  <i class="el-icon-document"></i>
                  充值明细
                </el-button>
                <el-button 
                  type="success" 
                  size="large" 
                  @click="$router.push('/points-consumption')"
                  class="consumption-btn"
                >
                  <i class="el-icon-data-analysis"></i>
                  消费明细
                </el-button>
              </div>
            </div>
            
            <!-- 支付图片弹窗 -->
            <el-dialog
              v-model="showPaymentDialog"
              :show-close="false"
              width="420px"
              class="payment-dialog"
            >
              <template #header>
                <div class="dialog-header">
                  <span>{{ paymentChannel === 'wechat' ? '微信支付' : '支付宝支付' }}</span>
                  <el-button link class="dialog-close" @click="handlePaymentClose">X</el-button>
                </div>
              </template>
              <div class="payment-body">
                <img :src="paymentImageUrl" alt="支付二维码" class="payment-img" />
                <div v-if="paymentOrderNo" class="payment-order">订单号：{{ paymentOrderNo }}</div>
                <div class="payment-tip">请使用{{ paymentChannel === 'wechat' ? '微信' : '支付宝' }}扫码完成支付</div>
              </div>
              <template #footer>
                <el-button type="primary" @click="handlePaymentClose">
                  确认支付结果
                </el-button>
              </template>
            </el-dialog>
          </div>
        </el-card>

        <!-- 充值记录 -->
        <el-card class="record-card" v-if="records.length > 0">
          <template #header>
            <div class="card-header">
              <h4 class="card-title">
                <i class="el-icon-tickets"></i>
                充值明细
              </h4>
              <el-button 
                type="text" 
                @click="loadRecords" 
                :loading="loadingRecords"
              >
                <i class="el-icon-refresh"></i>
                刷新
              </el-button>
            </div>
          </template>
          
          <el-table 
            :data="records" 
            stripe 
            class="record-table"
            v-loading="loadingRecords"
          >
            <el-table-column prop="order_no" label="订单号" min-width="210" align="center">
              <template #default="{ row }">
                <span>{{ row.order_no || row.id }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="points" label="积分" width="120" align="center">
              <template #default="{ row }">
                <span class="points-cell">{{ row.points }} 分</span>
              </template>
            </el-table-column>
            <el-table-column prop="amount" label="金额" width="100" align="center">
              <template #default="{ row }">
                <span class="amount-cell">¥{{ row.amount }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="channel" label="支付渠道" width="100" align="center">
              <template #default="{ row }">
                <el-tag size="small" :type="row.channel === 'wechat' ? 'success' : 'primary'">
                  {{ row.channel === 'wechat' ? '微信' : '支付宝' }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="status" label="状态" width="100" align="center">
              <template #default="{ row }">
                <el-tag 
                  :type="getStatusType(row.status)"
                  size="small"
                  effect="light"
                >
                  {{ getStatusText(row.status) }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="created_at" label="充值时间" min-width="180" />
            <el-table-column label="操作" width="200" align="center">
              <template #default="{ row }">
                <el-button 
                  size="small" 
                  @click="applyRefund(row)"
                  :disabled="!['completed', 'success'].includes(row.status)"
                  type="warning"
                  text
                >
                  申请退款
                </el-button>
                <el-button 
                  size="small" 
                  @click="checkRefund(row)"
                  type="info"
                  text
                >
                  退款状态
                </el-button>
                <el-button
                  v-if="row.status === 'pending' && row.order_no"
                  size="small"
                  type="primary"
                  text
                  @click="confirmExistingOrder(row)"
                >
                  确认支付
                </el-button>
              </template>
            </el-table-column>
          </el-table>
        </el-card>
      </el-col>

      <!-- 侧边栏 - 资源计费说明 -->
      <el-col :xs="24" :lg="8">
        <el-card class="pricing-card">
          <template #header>
            <div class="card-header">
              <h4 class="card-title">
                <i class="el-icon-cpu"></i>
                资源计费参考（参考）
              </h4>
            </div>
          </template>
          
          <div class="pricing-content">
            <div class="pricing-intro">
              <div class="intro-item">
                <i class="el-icon-time"></i>
                <span>每5分钟结算一个计费单元，创建时预冻结一小时额度</span>
              </div>
              <div class="intro-item">
                <i class="el-icon-switch-button"></i>
                <span>资源删除后停止计时，并结算当前5分钟计费单元</span>
              </div>
            </div>

            <el-table 
              :data="pricing" 
              stripe 
              class="pricing-table"
              size="small"
            >
              <el-table-column prop="vendor" label="云厂商" width="90" />
              <el-table-column prop="spec" label="规格" width="80" />
              <el-table-column label="积分/小时" width="100" align="center">
                <template #default="{ row }">
                  <span class="points-cost">{{ row.points_per_hour }} 分</span>
                </template>
              </el-table-column>
              <el-table-column label="100积分可用" width="120" align="center">
                <template #default="{ row }">
                  <span class="usage-time">{{ calculateUsageHours(row.points_per_hour) }} 小时</span>
                </template>
              </el-table-column>
            </el-table>

            <div class="pricing-summary">
              <div class="summary-item">
                <span class="label">当前充值:</span>
                <span class="value">{{ form.points }} 积分</span>
              </div>
              <div class="summary-item">
                <span class="label">可用时长 (1C2G):</span>
                <span class="value">{{ calculateTotalHours(oneC2GRate) }} 小时</span>
              </div>
            </div>

            <div class="pricing-updated">
              实时价格 · 最近更新：{{ pricingRefreshedAt || "正在获取" }}
            </div>

            <div class="pricing-note">
              <i class="el-icon-info"></i>
              💰 虚拟机不收启动费；计算、磁盘、商业镜像和出网流量按实际规则消耗积分，资源删除后停止计费
            </div>
          </div>
        </el-card>
      </el-col>
    </el-row>
  </div>
</template>

<script>
import axios from 'axios';
import { ElMessageBox } from 'element-plus';
export default {
  name: 'RechargePoints',
  data() {
    const email = (localStorage.getItem('email') || '').substring(4);
    return {
      form: { 
        email: email, 
        points: 500  // 默认充值500积分（5元）
      },
      records: [],
      balance: { points_balance: 0 },
      refreshing: false,
      submitting: false,
      loadingRecords: false,
      quickAmounts: [
        { points: 100, money: '1.00' },
        { points: 500, money: '5.00' },
        { points: 1000, money: '10.00' },
        { points: 2000, money: '20.00' },
        { points: 5000, money: '50.00' },
        { points: 10000, money: '100.00' }
      ],
      pricing: [],
      pricingRefreshedAt: '',
      pricingTimer: null,
      showPaymentDialog: false,
      paymentChannel: '',
      paymentImageUrl: '',
      paymentOrderNo: '',
      paymentSnapshot: null
    };
  },
  computed: {
    amountYuan() {
      return (this.form.points / 100).toFixed(2);
    },
    oneC2GRate() {
      return this.pricing.find(item => item.spec === '1C2G')?.points_per_hour || 1;
    }
  },
  methods: {
    async loadPricing() {
      try {
        const response = await axios.get('/api/points_pricing_catalog', {
          params: { system_disk_size: 40 }
        });
        this.pricing = response.data.prices || [];
        this.pricingRefreshedAt = response.data.refreshed_at || '';
      } catch (error) {
        if (error.response?.status !== 401) {
          this.$message.error('实时价格获取失败，请稍后刷新');
        }
      }
    },
    selectAmount(amount) {
      this.form.points = amount.points;
    },
    calculateUsageHours(pointsPerHour) {
      return (100 / pointsPerHour).toFixed(1);
    },
    calculateTotalHours(pointsPerHour) {
      return (this.form.points / pointsPerHour).toFixed(1);
    },
    getStatusType(status) {
      const typeMap = {
        'completed': 'success',
        'success': 'success',
        'pending': 'warning',
        'failed': 'danger',
        'refunding': 'info'
      };
      return typeMap[status] || 'info';
    },
    getStatusText(status) {
      const statusMap = {
        'completed': '已完成',
        'success': '已完成',
        'pending': '处理中',
        'failed': '失败',
        'refunding': '退款中'
      };
      return statusMap[status] || status;
    },
    async loadBalance() {
      this.refreshing = true;
      const email = (this.form.email || '').trim();
      if (!email) {
        this.refreshing = false;
        return;
      }
      try {
        const res = await axios.get('/api/points/balance', { params: { email } });
        this.balance = res.data || { points_balance: 0 };
        this.$message.success('余额已更新');
      } catch (e) {
        this.$message.error('获取余额失败');
      } finally {
        this.refreshing = false;
      }
    },
    async openPayment(channel) {
      const email = (this.form.email || '').trim();
      const points = Number(this.form.points);
      if (!email || points < 100 || points > 100000 || points % 100 !== 0) {
        this.$message.warning('充值积分必须是 100 到 100000 之间的整百数');
        return;
      }
      this.submitting = true;
      this.paymentChannel = channel;
      this.paymentImageUrl = channel === 'wechat'
        ? 'https://azlinux.oss-cn-zhangjiakou.aliyuncs.com/sre/bg/wechat_pay.png'
        : 'https://azlinux.oss-cn-zhangjiakou.aliyuncs.com/sre/bg/ali_pay.png';
      try {
        const response = await axios.post('/api/points/recharge', {
          email,
          points,
          channel
        });
        this.paymentOrderNo = response.data.order_no;
        this.paymentSnapshot = {
          points: response.data.points,
          amount: response.data.amount,
          channel: response.data.channel
        };
        this.showPaymentDialog = true;
      } catch (error) {
        this.$message.error(error.response?.data?.error || '创建充值订单失败');
      } finally {
        this.submitting = false;
      }
    },
    async handlePaymentClose() {
      if (!this.paymentOrderNo) {
        this.showPaymentDialog = false;
        return;
      }
      try {
        await ElMessageBox.confirm(
          `是否已成功支付 ¥${Number(this.paymentSnapshot.amount).toFixed(2)}？`,
          '确认支付结果',
          {
            confirmButtonText: '我已完成支付',
            cancelButtonText: '支付未成功',
            type: 'warning'
          }
        );
        const response = await axios.post(
          `/api/points/recharge/${this.paymentOrderNo}/confirm`,
          { paid: true }
        );
        this.$message.success(response.data.message || '充值成功！积分已到账');
        this.balance.points_balance = response.data.points_balance;
      } catch (error) {
        if (error === 'cancel' || error === 'close') {
          try {
            await axios.post(
              `/api/points/recharge/${this.paymentOrderNo}/confirm`,
              { paid: false }
            );
            this.$message.info('本次未增加积分');
          } catch (confirmError) {
            this.$message.error(
              confirmError.response?.data?.error || '更新订单状态失败'
            );
          }
        } else {
          this.$message.error(error.response?.data?.error || '充值确认失败');
        }
      } finally {
        this.showPaymentDialog = false;
        this.paymentOrderNo = '';
        this.paymentSnapshot = null;
        this.loadRecords();
        this.loadBalance();
      }
    },
    async confirmExistingOrder(row) {
      try {
        await ElMessageBox.confirm(
          `确认订单 ${row.order_no} 已支付 ¥${Number(row.amount).toFixed(2)}？`,
          '确认支付结果',
          {
            confirmButtonText: '我已完成支付',
            cancelButtonText: '取消',
            type: 'warning'
          }
        );
        const response = await axios.post(
          `/api/points/recharge/${row.order_no}/confirm`,
          { paid: true }
        );
        this.$message.success(response.data.message);
        await Promise.all([this.loadRecords(), this.loadBalance()]);
      } catch (error) {
        if (error !== 'cancel' && error !== 'close') {
          this.$message.error(error.response?.data?.error || '充值确认失败');
        }
      }
    },
    async loadRecords() {
      this.loadingRecords = true;
      const email = (this.form.email || '').trim();
      if (!email) {
        this.loadingRecords = false;
        return;
      }
      try {
        const res = await axios.get('/api/points/recharge_records', { params: { email } });
        this.records = res.data || [];
      } catch (e) {
        this.$message.error('获取记录失败');
      } finally {
        this.loadingRecords = false;
      }
    },
    async applyRefund(row) {
      try {
        await axios.post('/api/points/refund', { 
          email: this.form.email, 
          recharge_id: row.id 
        });
        this.$message.success('退款申请已提交，24小时内处理');
        this.loadRecords();
      } catch (e) {
        this.$message.error('退款申请失败');
      }
    },
    async checkRefund(row) {
      try {
        const res = await axios.get('/api/points/refund_status', { 
          params: { 
            email: this.form.email, 
            recharge_id: row.id 
          } 
        });
        const status = res.data.status || '未知';
        this.$message.info(`退款状态: ${this.getStatusText(status)}`);
      } catch (e) {
        this.$message.error('查询失败');
      }
    }
  },
  mounted() {
    this.loadBalance();
    this.loadPricing();
    this.pricingTimer = window.setInterval(this.loadPricing, 60000);
  },
  beforeUnmount() {
    if (this.pricingTimer) window.clearInterval(this.pricingTimer);
  }
};
</script>

<style scoped>
.recharge-points {
  max-width: 1400px;
  margin: 0 auto;
  padding: 20px;
}

/* 顶部余额卡片 */
.header-section {
  margin-bottom: 20px;
}

.balance-card {
  border-radius: 12px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
}

.balance-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.balance-info {
  text-align: left;
}

.balance-label {
  font-size: 14px;
  opacity: 0.9;
  margin-bottom: 8px;
}

.balance-value {
  font-size: 32px;
  font-weight: 700;
}

.balance-value .unit {
  font-size: 16px;
  font-weight: 500;
  margin-left: 4px;
}

.refresh-btn {
  color: white !important;
  background: rgba(255, 255, 255, 0.2) !important;
  border: none;
}

/* 卡片通用样式 */
.el-card {
  border-radius: 12px;
  border: none;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.06);
  margin-bottom: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 !important;
}

.card-title {
  color: #303133;
  margin: 0;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
}

/* 主充值卡片 */
.recharge-card {
  min-height: 600px;
}

.usage-alert {
  margin-bottom: 24px;
  border-radius: 8px;
}

.recharge-form {
  padding: 0 8px;
}

.form-section {
  margin-bottom: 32px;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
}

/* 快捷金额选择 */
.quick-amounts {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-bottom: 24px;
}

.amount-option {
  border: 2px solid #e4e7ed;
  border-radius: 8px;
  padding: 16px 12px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  background: white;
}

.amount-option:hover {
  border-color: #409EFF;
  transform: translateY(-2px);
}

.amount-option.active {
  border-color: #409EFF;
  background: #f0f9ff;
  box-shadow: 0 4px 12px rgba(64, 158, 255, 0.15);
}

.amount-option .points {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 4px;
}

.amount-option .money {
  font-size: 14px;
  color: #67C23A;
  font-weight: 500;
}

/* 自定义金额 */
.custom-amount {
  margin-top: 24px;
}

.custom-label {
  font-size: 14px;
  color: #606266;
  margin-bottom: 12px;
  font-weight: 500;
}

.custom-inputs {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.amount-display {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.amount-text {
  font-size: 18px;
  font-weight: 600;
  color: #67C23A;
}

.exchange-rate {
  font-size: 12px;
  color: #909399;
}

/* 操作按钮 */
.action-buttons {
  margin-top: 40px;
  text-align: center;
}

.primary-actions {
  display: flex;
  justify-content: center;
  gap: 16px;
  flex-wrap: wrap;
}

.recharge-btn {
  border: none;
  padding: 16px 48px;
  font-weight: 600;
  font-size: 16px;
  height: auto;
  box-shadow: 0 4px 15px rgba(64, 158, 255, 0.3);
}

.recharge-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(64, 158, 255, 0.4);
}

.wechat-btn {
  background-color: #09bb07;
  border-color: #09bb07;
}

.wechat-btn:hover {
  background-color: #07a306;
  border-color: #07a306;
}

.alipay-btn {
  background-color: #1677ff;
  border-color: #1677ff;
}

.alipay-btn:hover {
  background-color: #0f5fd1;
  border-color: #0f5fd1;
}

.secondary-actions {
  margin-top: 16px;
  display: flex;
  gap: 12px;
  justify-content: center;
}

.payment-dialog :deep(.el-dialog__body) {
  padding: 0 20px 20px 20px;
}
.dialog-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
}
.dialog-close {
  font-weight: 700;
  color: #909399;
}
.payment-body {
  padding: 8px;
}
.payment-img {
  width: 100%;
  height: auto;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0,0,0,0.08);
  margin-bottom: 10px;
}
.payment-tip {
  text-align: center;
  color: #606266;
  font-size: 13px;
}

/* 记录表格 */
.record-table {
  width: 100%;
}

.points-cell {
  color: #E6A23C;
  font-weight: 600;
}

.amount-cell {
  color: #67C23A;
  font-weight: 600;
}

/* 计费说明卡片 */
.pricing-content {
  padding: 8px 0;
}

.pricing-intro {
  margin-bottom: 20px;
}

.intro-item {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  color: #606266;
  font-size: 13px;
}

.pricing-table {
  margin: 16px 0;
}

.points-cost {
  color: #F56C6C;
  font-weight: 600;
}

.usage-time {
  color: #409EFF;
  font-weight: 600;
}

.pricing-summary {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 16px;
  margin: 20px 0;
}

.summary-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.summary-item:last-child {
  margin-bottom: 0;
}

.summary-item .label {
  color: #606266;
  font-size: 13px;
}

.summary-item .value {
  color: #303133;
  font-weight: 600;
}

.pricing-note {
  background: #f0f9ff;
  border: 1px solid #c6e2ff;
  border-radius: 6px;
  padding: 12px;
  color: #409EFF;
  font-size: 13px;
  line-height: 1.4;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .recharge-points {
    padding: 12px;
  }
  
  .quick-amounts {
    grid-template-columns: repeat(2, 1fr);
  }
  
  .custom-inputs {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .secondary-actions {
    flex-direction: column;
  }
  
  .balance-content {
    flex-direction: column;
    gap: 16px;
    text-align: center;
  }
}

@media (max-width: 480px) {
  .quick-amounts {
    grid-template-columns: 1fr;
  }
}
</style>
